const API_BASE = process.env.REACT_APP_API_URL || '/api';

class ApiClient {
  constructor() {
    this.token = localStorage.getItem('wr_token');
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('wr_token', token);
    } else {
      localStorage.removeItem('wr_token');
    }
  }

  getToken() {
    return this.token || localStorage.getItem('wr_token');
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      ...options,
      headers,
    };

    if (options.body && typeof options.body === 'object') {
      config.body = JSON.stringify(options.body);
    }

    let response;
    try {
      response = await fetch(url, config);
    } catch (err) {
      throw new Error('Network error - is the backend server running?');
    }

    if (response.status === 401) {
      this.setToken(null);
      window.dispatchEvent(new CustomEvent('wr:unauthorized'));
    }

    // Check content type to avoid parsing HTML as JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      if (!response.ok) {
        throw new Error(`Server error (${response.status}) - is the backend server running on port 3059?`);
      }
      // For successful responses that aren't JSON, return empty object
      return {};
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    // Tolerate empty bodies (truncated/cached responses) instead of failing JSON.parse
    const text = await response.text();
    return text ? JSON.parse(text) : {};
  }

  get(endpoint, params = {}) {
    // Filter out undefined/null values to prevent "undefined" strings in URL
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
    );
    const query = new URLSearchParams(filteredParams).toString();
    const url = query ? `${endpoint}?${query}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  post(endpoint, body) {
    return this.request(endpoint, { method: 'POST', body });
  }

  put(endpoint, body) {
    return this.request(endpoint, { method: 'PUT', body });
  }

  delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }

  // Upload file with multipart form data
  async upload(endpoint, formData, method = 'POST') {
    const url = `${API_BASE}${endpoint}`;

    const headers = {};
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method,
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Upload failed');
    }

    return response.json();
  }
}

export const apiClient = new ApiClient();
export default apiClient;
